// ── Optimizer Page ────────────────────────────────────────────────────
import React, { useState, useEffect } from 'react';
import { optimizerAPI, costAPI } from '../services/api';

export function Optimizer() {
  const [result,   setResult]   = useState(null);
  const [savings,  setSavings]  = useState(null);
  const [opts,     setOpts]     = useState(null);
  const [loading,  setLoading]  = useState(false);
  const [form,     setForm]     = useState({ cpu:2, memory:4, hours:720, storage:50, workload_type:'general' });

  useEffect(() => {
    Promise.all([optimizerAPI.savings(), costAPI.optimize()])
      .then(([s, o]) => { 
        setSavings(s.data?.data || s.data); 
        setOpts(o.data); 
      })
      .catch(console.error);
  }, []);

  const handleRecommend = async e => {
    e.preventDefault(); setLoading(true);
    try {
      const r = await optimizerAPI.recommend(form);
      const payload = r.data?.data || r.data;
      setResult({
        recommended: payload.recommended,
        savings: payload.savings,
        rankings: Array.isArray(payload.rankings) ? payload.rankings : [],
        workload_type: payload.workload_type,
        specs: payload.specs,
      });
    } catch (err) {
      console.error(err);
    } finally { setLoading(false); }
  };

  const COLORS = { aws:'#ff9900', gcp:'#4285f4', azure:'#0078d4', digitalocean:'#0080ff', vultr:'#007bfc', lightnode:'#00c853' };

  return (
    <div>
      <div className="page-header">
        <div><h1>⚡ Cost Optimizer</h1><p>ML-powered cloud cost recommendations</p></div>
      </div>

      {/* Potential savings */}
      {savings && (
        <div className="card" style={{ marginBottom:20, borderTop:'3px solid var(--green)' }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
            <h2 style={{ fontSize:16 }}>💰 Potential Monthly Savings</h2>
            <span style={{ fontSize:28, fontWeight:700, color:'var(--green)' }}>${savings.total_savings?.toFixed(2)}</span>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(200px,1fr))', gap:12 }}>
            {savings.data?.map((s, i) => (
              <div key={i} style={{ background:'var(--bg-secondary)', borderRadius:10, padding:14 }}>
                <div style={{ fontWeight:600, fontSize:14, marginBottom:4 }}>{s.name}</div>
                <div style={{ fontSize:24, fontWeight:700, color:'var(--green)' }}>${s.saving}</div>
                <span className={`badge ${s.effort==='low'?'badge-green':s.effort==='medium'?'badge-yellow':'badge-red'}`} style={{ fontSize:10, marginTop:6 }}>
                  {s.effort} effort
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20 }}>
        {/* Recommendation form */}
        <div className="card">
          <h2 style={{ fontSize:16, marginBottom:20 }}>Get AI Recommendation</h2>
          <form onSubmit={handleRecommend}>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
              {[['cpu','CPU vCPUs'],['memory','Memory GB'],['hours','Hours/month'],['storage','Storage GB']].map(([k,l]) => (
                <div className="form-group" key={k} style={{ marginBottom:0 }}>
                  <label>{l}</label>
                  <input className="form-control" type="number" min="1" value={form[k]}
                    onChange={e => setForm({ ...form, [k]: +e.target.value })} />
                </div>
              ))}
            </div>
            <div className="form-group" style={{ marginTop:12 }}>
              <label>Workload Type</label>
              <select className="form-control" value={form.workload_type}
                onChange={e => setForm({ ...form, workload_type: e.target.value })}>
                {['general','compute-intensive','memory-intensive','storage-intensive','batch','cicd'].map(t => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
            <button className="btn btn-primary" type="submit" disabled={loading} style={{ width:'100%', marginTop:8 }}>
              {loading ? 'Analyzing...' : '⚡ Get Recommendation'}
            </button>
          </form>
        </div>

        {/* Result */}
        <div className="card">
          <h2 style={{ fontSize:16, marginBottom:20 }}>Recommendation Result</h2>
          {!result ? (
            <div className="empty"><div className="empty-icon">🤖</div><h3>Run analysis</h3><p>Fill in workload specs and click Get Recommendation</p></div>
          ) : (
            <>
              <div style={{ padding:16, background:'var(--green-bg)', borderRadius:10, border:'1px solid rgba(16,185,129,0.2)', marginBottom:16 }}>
                <div style={{ fontSize:12, color:'#4d6080' }}>RECOMMENDED PROVIDER</div>
                <div style={{ fontSize:28, fontWeight:700, color: COLORS[result.recommended], textTransform:'uppercase', fontFamily:'var(--font-mono)' }}>{result.recommended}</div>
                <div style={{ fontSize:13, color:'var(--green)', marginTop:4 }}>Saves ${result.savings}/month vs most expensive</div>
              </div>
              {result.rankings?.map((r, i) => (
                <div key={r.provider} style={{ display:'flex', alignItems:'center', gap:12, padding:'10px 0', borderBottom:'1px solid var(--border)' }}>
                  <span style={{ width:24, fontSize:18 }}>{['🥇','🥈','🥉','4️⃣'][i]}</span>
                  <span style={{ width:100, fontSize:12, fontFamily:'var(--font-mono)', textTransform:'uppercase', color: COLORS[r.provider], fontWeight:700 }}>{r.provider}</span>
                  <div style={{ flex:1, height:6, background:'var(--bg-hover)', borderRadius:3 }}>
                    <div style={{ width:`${(r.confidence)}%`, height:'100%', background: COLORS[r.provider], borderRadius:3 }} />
                  </div>
                  <span style={{ fontFamily:'var(--font-mono)', fontSize:13, fontWeight:700, width:80, textAlign:'right' }}>${r.monthly_cost}/mo</span>
                  <span style={{ fontSize:11, color:'#4d6080', width:40 }}>{r.confidence}%</span>
                </div>
              ))}
            </>
          )}
        </div>
      </div>

      {/* Per-deployment optimizations */}
      {opts?.data?.length > 0 && (
        <div className="card" style={{ marginTop:20 }}>
          <h2 style={{ fontSize:16, marginBottom:16 }}>Deployment-Level Recommendations</h2>
          <div className="table-wrap">
            <table>
              <thead><tr><th>Deployment</th><th>Current Cloud</th><th>Recommended</th><th>Est. Monthly Savings</th><th>Reason</th></tr></thead>
              <tbody>
                {opts.data.map(d => (
                  <tr key={d.deploymentId}>
                    <td><strong>{d.name}</strong></td>
                    <td><span style={{ color: COLORS[d.currentCloud], fontFamily:'var(--font-mono)', textTransform:'uppercase', fontSize:12 }}>{d.currentCloud}</span></td>
                    <td><span style={{ color: COLORS[d.recommended], fontFamily:'var(--font-mono)', textTransform:'uppercase', fontSize:12, fontWeight:700 }}>{d.recommended}</span></td>
                    <td><span style={{ color:'var(--green)', fontWeight:700 }}>${d.estimatedSavings}</span></td>
                    <td style={{ color:'#8899b4', fontSize:13 }}>{d.reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default Optimizer;
